/**
 * 导航与路径规划全局配置
 * 将关键参数集中管理，避免散落在多个模块中难以维护
 */

export const NavConfig = {
  // 路径平滑相关配置（A* 找到原始路径后，在主线程做二次平滑时生效）
  smoothing: {
    // 可见性平滑的安全裕度（单位与坐标一致，需与图构建 safetyMargin 对齐）
    // 调大：路径会更远离障碍，更安全但更难被拉直，可能出现“有空隙但视线被判为阻塞”；
    // 调小：路径会更贴近障碍，更短但贴边风险更高，一般保持与图构建的 2.0 一致即可。
    clearance: 2.0,
    // 拐点惩罚权重：值越大越偏好更少折点、更接近直线的路径；值越小越接近“只看总长度”的效果
    // 调大：会更积极地删除中间节点，但仍严格遵守障碍与 mandatoryWaypoints 约束；
    // 调小：路径会更忠实于原始路网，转弯点保留更多。
    turnPenaltyWeight: 8,
    // 同层段内最大回看步长（节点数），控制 DP 平滑时能一次“跳过”多远的节点
    // 调大：平滑更全局，潜在更直，但计算量和耗时增加；调小：只做局部修剪，折点会更多。
    maxLookahead: 100,
    // 可选的超时保护（毫秒），0 表示不启用
    // 设置为 >0 时，平滑在耗时接近该阈值会提前停止，优先保证响应速度，可能留下少量未优化折点。
    timeBudgetMs: 0,
    // 是否按楼层分别使用各自障碍物做视线检测
    // true：每层只受本层障碍影响（推荐，多楼层互不干扰）；false：回退为使用全局 obstacles，
    //       可能出现 L1 被 L0 的障碍“投影”影响的情况，仅用于调试或兼容。
    useObstaclesByLayer: true,
    // 是否在跨层出口后保留一个缓冲节点，保证路径视觉上“走出楼梯”
    // true：楼梯/电梯出口后会强制保留第一个节点，看起来有一个“离开竖井”的缓冲折点；
    // false：平滑可能直接把路径拉到更远的节点，视觉上更直，但楼梯口附近会更“贴边”。
    protectTransitionBuffer: true,
  },

  // 图构建相关配置（主要影响质心自由空间网络的稠密程度）
  graph: {
    // 质心额外连边：在三角质心生成基础连边后，再在局部范围内补充一些可见边，提升连通性
    extraEdges: {
      // 是否启用额外连边
      // true：在每个质心节点周围尝试补充少量短边，使原本度数较低的点也有更多可选路径；
      // false：完全回退到仅使用 Delaunay 相邻三角形之间的连边（当前线上行为）。
      enabled: true,
      // 每个节点希望达到的最小度数（已满足该度数的节点默认不再强制补边）
      // 调大：单点的出度会更高，图更稠密；调小：只修补“孤立/度数过低”的节点。
      minDegreePerNode: 2,
      // 每个节点允许的最大度数上限，用于防止在空旷区域连出“几乎完全图”。
      // 建议控制在 4~8 之间，既保证连通性又不至于让边数爆炸。
      maxDegreePerNode: 6,
      // 额外连边的最大长度因子：maxLength = 当前图中“已有边平均长度” × maxLengthFactor
      // 调大：可以跨更远的空隙连边，边会明显增多；调小：只在局部补几条短边，图更精细但更保守。
      maxLengthFactor: 2.5,
    },
  },

  // 连接器 / 通道相关配置（影响“生成导航图”时障碍的分布，会改变楼梯/电梯通道周围的空白范围）
  corridor: {
    // 是否启用通道避障逻辑
    // true：在每次生成导航图时，为楼梯/电梯周围以及其通道方向预留一条“安全走廊”，禁止生成障碍；
    // false：不做特殊处理，通道附近也可能被障碍占满，仅建议在调试或做对比测试时使用。
    enabled: true,
    // 通道总长度（从 access 口沿 entranceAngle 方向，单位：像素）
    // 决定从楼梯/电梯 access 口向外清理出多长的一段走廊；需 >= step 才会真正生成走廊采样点。
    length: 40,
    // 通道半宽（圆形采样半径，单位：像素）
    // 调大：走廊更宽，障碍会离通道更远，安全但可放障碍区域变少；调小：走廊更窄，障碍会更靠近通道。
    halfWidth: 12,
    // 竖井中心避让半径（每层竖井中心 c.x/c.y 周围清理出的圆形区域）
    // 决定楼梯/电梯竖井本体周围的大致空白范围，通常与建筑竖井实际尺寸相关。
    shaftRadius: 17,
    // access 入口周围避让半径（accessX/accessY 周围的圆形区域）
    // 直接决定楼梯口/电梯口附近“人能站立和转弯”的安全空间大小，是最直观影响通道出入口空白的参数。
    accessRadius: 10,
    // 通道内采样步长（用于构建 avoidZones 和调试采样）
    // 步长越小，沿通道方向的圆形避让区越密集，走廊覆盖更连续；越大则只在少数点清空，通道更“离散”。
    step: 20,
  },

  // 调试与告警开关（只影响日志输出，不直接改变路径和障碍，只用于定位问题）
  diagnostics: {
    // 当某个楼层缺少 obstaclesByLayer 数据时打印告警
    // 用于发现“某层没有自己的障碍而悄悄退回使用全局 obstacles”的情况，例如 L1 被 L0 阴影影响。
    warnMissingLayerObstacles: true,
    // Worker 端检查通道是否被障碍阻塞并打印结果
    // 若通道避让逻辑生效却仍有障碍与通道避让区相交，会在控制台输出 [Corridor] 警告；
    // 关闭可减少日志噪音，对性能影响极小。
    checkCorridorCollisions: true,
  },
};
